import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, createBlock as _createBlock, withCtx as _withCtx, mergeProps as _mergeProps } from "vue"

const _hoisted_1 = { class: "w-full pr-10" }
const _hoisted_2 = {
  "data-testid": "sheet-template-body",
  class: "flex flex-col gap-5 p-4 w-full overflow-y-auto text-left"
}
const _hoisted_3 = {
  key: 0,
  "data-testid": "sheet-template-format-notice",
  role: "status",
  class: "flex gap-3 rounded-lg border border-primary bg-primary5 px-4 py-3 text-sm text-neutral50"
}
const _hoisted_4 = { class: "flex flex-col gap-1" }
const _hoisted_5 = /*#__PURE__*/_createElementVNode("p", { class: "text-sm text-neutral50" }, " Configure as colunas do modelo de folha, de cima para baixo, na ordem que são exibidos. As colunas fixas são obrigatórias e não podem ser alteradas. ", -1)
const _hoisted_6 = { class: "flex flex-col gap-2" }
const _hoisted_7 = { class: "flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-neutral50" }
const _hoisted_8 = { class: "rounded-full bg-primary5 px-2 py-0.5 text-xs font-bold text-primary" }
const _hoisted_9 = { class: "rounded-lg border border-gray-200 overflow-hidden" }
const _hoisted_10 = { class: "flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md bg-gray-200 text-xs font-extrabold text-neutral50" }
const _hoisted_11 = { class: "flex-1 text-sm font-medium text-neutral50" }
const _hoisted_12 = { class: "flex w-[84px] justify-end" }
const _hoisted_13 = /*#__PURE__*/_createElementVNode("span", { class: "sr-only" }, "Coluna fixa", -1)
const _hoisted_14 = { class: "flex flex-col gap-2" }
const _hoisted_15 = { class: "flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-neutral50" }
const _hoisted_16 = { class: "rounded-full bg-primary5 px-2 py-0.5 text-xs font-bold text-primary" }
const _hoisted_17 = {
  key: 0,
  "data-testid": "additional-columns-empty",
  class: "rounded-lg border border-dashed border-gray-300 px-3 py-4 text-center text-sm text-gray-500"
}
const _hoisted_18 = {
  "data-testid": "additional-column-row",
  class: "flex items-center gap-3 border-b border-gray-100 bg-white px-3 py-2 last:border-b-0"
}
const _hoisted_19 = ["aria-label", "onKeydown"]
const _hoisted_20 = { class: "flex w-[84px] justify-end" }
const _hoisted_21 = ["onClick"]
const _hoisted_22 = { class: "flex justify-end gap-4" }

import { computed, nextTick, ref, watch, PropType } from 'vue'
import draggable from 'vuedraggable'
import {
  mdiDragVertical,
  mdiInformationOutline,
  mdiLock,
  mdiPlus,
  mdiTrashCanOutline
} from '@mdi/js'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import EditableHeader from '@/components/designSystem/Inputs/EditableHeader.vue'
import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import SVGIcon from '@/components/SVGIcon.vue'
import { DSAttendanceMonitoringType } from '@/constants/ds-attendance.constants'
import {
  ADDITIONAL_COLUMN_OPTIONS,
  buildTemplateColumns,
  columnTypeLabel,
  fixedColumnsFor,
  formatNotice,
  loadTemplateColumns
} from '@/components/designSystem/modals/sheetTemplateColumns'

// Tipagens alinhadas com o Payload do Backend (contrato inalterado — #3052)
interface SheetTemplateFields {
  columns: string[]
  rows: number
}

interface SheetData {
  id?: number
  name: string
  templateFields: SheetTemplateFields
}

interface AdditionalColumn {
  id: number
  name: string
  /** Nome fora da lista fechada com que a linha foi carregada: segue oferecido só nesta linha. */
  originalName: string | null
}


export default /*#__PURE__*/_defineComponent({
  __name: 'SheetTemplateModal',
  props: {
  show: { type: Boolean, required: true },
  initialData: { type: Object as PropType<SheetData | null>, default: null },
  monitoringType: {
    type: String as PropType<DSAttendanceMonitoringType>,
    default: DSAttendanceMonitoringType.FOLHA
  }
},
  emits: ['close', 'save'],
  setup(__props, { emit }) {

const props = __props;





const OPTIONS: readonly string[] = ADDITIONAL_COLUMN_OPTIONS

const modelName = ref('Novo Modelo de Folha')
const templateId = ref<number | undefined>(undefined)
const additional = ref<AdditionalColumn[]>([])
const notice = ref<string[]>([])
const additionalList = ref<HTMLElement | null>(null)
let nextRowId = 1

const fixedColumns = computed(() => fixedColumnsFor(props.monitoringType))

const isLegacyName = (name: string) => name !== '' && !OPTIONS.includes(name)

const toRow = (name: string): AdditionalColumn => ({
  id: nextRowId++,
  name,
  originalName: isLegacyName(name) ? name : null
})

watch(
  () => props.show,
  (isShowing) => {
    if (!isShowing) return
    if (props.initialData?.templateFields) {
      const loaded = loadTemplateColumns(
        props.initialData.templateFields.columns,
        props.monitoringType
      )
      templateId.value = props.initialData.id
      modelName.value = props.initialData.name
      additional.value = loaded.additional.map(toRow)
      notice.value = formatNotice(loaded, props.monitoringType)
    } else {
      templateId.value = undefined
      modelName.value = 'Novo Modelo de Folha'
      additional.value = []
      notice.value = []
    }
  },
  // Montado já aberto (ex.: stories) também carrega o estado.
  { immediate: true }
)

const filledAdditional = computed(() => additional.value.filter((row) => row.name.trim() !== ''))
// "Antiga" vale pelo nome ATUAL da linha: trocada para uma opção, passa a ocupar essa opção (Q6).
const usedOptions = computed(() =>
  additional.value.filter((row) => OPTIONS.includes(row.name)).map((row) => row.name)
)
// Linhas vazias contam: cada uma ainda vai receber uma das opções (Q6).
const allOptionsUsed = computed(
  () => additional.value.filter((row) => !isLegacyName(row.name)).length >= OPTIONS.length
)

const optionsFor = (row: AdditionalColumn) => {
  const options = OPTIONS.map((option) => ({
    title: option,
    value: option,
    disabled: option !== row.name && usedOptions.value.includes(option)
  }))
  return row.originalName
    ? [{ title: row.originalName, value: row.originalName, disabled: false }, ...options]
    : options
}

// O type-ahead do VSelect 3.3 escolhe pelo prefixo do título ignorando `disabled` (Q6):
// valor em uso em outra linha é recusado; o nome antigo da própria linha continua permitido.
const setRowName = (row: AdditionalColumn, value: string | null) => {
  const name = value ?? ''
  const usedElsewhere = additional.value.some((other) => other !== row && other.name === name)
  if (name !== '' && name !== row.originalName && usedElsewhere) return
  row.name = name
}

// Nome livre antigo é sempre texto (Q2), mesmo que o nome lembre uma coluna de hora.
const badgeFor = (row: AdditionalColumn) =>
  isLegacyName(row.name) ? 'Texto' : columnTypeLabel(row.name)

const addColumn = () => {
  if (allOptionsUsed.value) return
  additional.value.push(toRow(''))
}

// ↑/↓ na alça move uma posição, só dentro das adicionais (padrão do SourcePrecedenceList).
const onHandleKeydown = (event: KeyboardEvent, index: number) => {
  if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
  event.preventDefault()
  const target = event.key === 'ArrowUp' ? index - 1 : index + 1
  if (target < 0 || target >= additional.value.length) return
  const next = [...additional.value]
  const [moved] = next.splice(index, 1)
  next.splice(target, 0, moved)
  additional.value = next
  // A linha movida é recriada no DOM e perde o foco; devolve o foco à alça dela.
  nextTick(() => {
    const handles = additionalList.value?.querySelectorAll<HTMLElement>(
      '[data-testid="additional-column-handle"]'
    )
    handles?.[target]?.focus()
  })
}

const close = () => emit('close')

const save = () => {
  emit('save', {
    id: templateId.value,
    name: modelName.value,
    templateFields: {
      columns: buildTemplateColumns(
        props.monitoringType,
        additional.value.map((row) => row.name)
      ),
      // RN-09: o modelo não dimensiona linhas; edição preserva o valor salvo, criação nasce com 1.
      rows: props.initialData?.templateFields?.rows ?? 1
    }
  })
}

return (_ctx: any,_cache: any) => {
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_tooltip = _resolveComponent("v-tooltip")!

  return (_openBlock(), _createBlock(ModalVue, {
    modalActive: __props.show,
    maxWidth: "max-w-[min(808px,92vw)]",
    maxHeight: "88vh",
    hasHeaderBorder: "",
    hasFooterBorder: "",
    onClose: close
  }, {
    header: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(EditableHeader, {
          modelValue: modelName.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((modelName).value = $event)),
          placeholder: "Nome do modelo"
        }, null, 8, ["modelValue"])
      ])
    ]),
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_22, [
        _createVNode(OutlinedButton, {
          text: "Fechar",
          width: "100px",
          height: "42px",
          class: "text-primary border-primary",
          onClick: close
        }),
        _createVNode(FilledButton, {
          "data-testid": "sheet-template-save-button",
          text: "Salvar",
          width: "100px",
          height: "42px",
          color: "primary",
          onClick: save
        })
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_2, [
        (notice.value.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createVNode(SVGIcon, {
                path: _unref(mdiInformationOutline),
                class: "h-5 w-5 shrink-0 text-primary"
              }, null, 8, ["path"]),
              _createElementVNode("div", _hoisted_4, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(notice.value, (line) => {
                  return (_openBlock(), _createElementBlock("p", { key: line }, _toDisplayString(line), 1))
                }), 128))
              ])
            ]))
          : _createCommentVNode("", true),
        _hoisted_5,
        _createElementVNode("section", _hoisted_6, [
          _createElementVNode("h3", _hoisted_7, [
            _createTextVNode(" Colunas fixas "),
            _createElementVNode("span", _hoisted_8, _toDisplayString(fixedColumns.value.length), 1)
          ]),
          _createElementVNode("div", _hoisted_9, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(fixedColumns.value, (column, index) => {
              return (_openBlock(), _createElementBlock("div", {
                key: column,
                "data-testid": "fixed-column-row",
                class: "flex items-center gap-3 border-b border-gray-100 bg-gray-50 px-3 py-2.5 last:border-b-0"
              }, [
                _createElementVNode("span", _hoisted_10, _toDisplayString(index + 1), 1),
                _createElementVNode("span", _hoisted_11, _toDisplayString(column), 1),
                _createElementVNode("div", _hoisted_12, [
                  _createVNode(LabelBadge, {
                    label: _unref(columnTypeLabel)(column),
                    color: "gray",
                    size: "small",
                    icon: null
                  }, null, 8, ["label"])
                ]),
                _createVNode(SVGIcon, {
                  path: _unref(mdiLock),
                  class: "h-5 w-5 shrink-0 text-neutralLight"
                }, null, 8, ["path"]),
                _hoisted_13
              ]))
            }), 128))
          ])
        ]),
        _createElementVNode("section", _hoisted_14, [
          _createElementVNode("h3", _hoisted_15, [
            _createTextVNode(" Colunas adicionais "),
            _createElementVNode("span", _hoisted_16, _toDisplayString(filledAdditional.value.length), 1)
          ]),
          (!additional.value.length)
            ? (_openBlock(), _createElementBlock("p", _hoisted_17, " Nenhuma coluna adicional. Use \"Adicionar coluna\" abaixo. "))
            : (_openBlock(), _createElementBlock("div", {
                key: 1,
                ref_key: "additionalList",
                ref: additionalList,
                class: "rounded-lg border border-gray-200 overflow-hidden"
              }, [
                _createVNode(_unref(draggable), {
                  modelValue: additional.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((additional).value = $event)),
                  "item-key": "id",
                  handle: ".additional-column-handle",
                  "ghost-class": "bg-primary5",
                  animation: "200",
                  tag: "div"
                }, {
                  item: _withCtx(({ element, index }) => [
                    _createElementVNode("div", _hoisted_18, [
                      _createElementVNode("button", {
                        type: "button",
                        "data-testid": "additional-column-handle",
                        class: "additional-column-handle cursor-grab rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary20",
                        "aria-label": `${element.name || 'Coluna sem nome'}, posição ${fixedColumns.value.length + index + 1}. Use as setas para mover`,
                        onKeydown: ($event: any) => (onHandleKeydown($event, index))
                      }, [
                        _createVNode(SVGIcon, {
                          path: _unref(mdiDragVertical),
                          class: "h-5 w-5 text-neutralLight"
                        }, null, 8, ["path"])
                      ], 40, _hoisted_19),
                      _createVNode(_component_v_select, {
                        "model-value": element.name,
                        "data-testid": "additional-column-select",
                        items: optionsFor(element),
                        "item-props": "",
                        placeholder: "Selecionar coluna",
                        variant: "outlined",
                        density: "compact",
                        "hide-details": "",
                        class: "flex-1",
                        "onUpdate:modelValue": ($event: any) => (setRowName(element, $event))
                      }, null, 8, ["model-value", "items", "onUpdate:modelValue"]),
                      _createElementVNode("div", _hoisted_20, [
                        (element.name)
                          ? (_openBlock(), _createBlock(LabelBadge, {
                              key: 0,
                              label: badgeFor(element),
                              color: "gray",
                              size: "small",
                              icon: null
                            }, null, 8, ["label"]))
                          : _createCommentVNode("", true)
                      ]),
                      _createElementVNode("button", {
                        type: "button",
                        "data-testid": "remove-column-button",
                        class: "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-negative hover:bg-red-50",
                        "aria-label": "Excluir coluna",
                        onClick: ($event: any) => (additional.value.splice(index, 1))
                      }, [
                        _createVNode(SVGIcon, {
                          path: _unref(mdiTrashCanOutline),
                          class: "h-5 w-5"
                        }, null, 8, ["path"])
                      ], 8, _hoisted_21)
                    ])
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ], 512)),
          _createVNode(_component_v_tooltip, {
            disabled: !allOptionsUsed.value,
            text: "Todas as colunas disponíveis já foram adicionadas",
            location: "top"
          }, {
            activator: _withCtx(({ props: tooltipProps }) => [
              _createElementVNode("div", _mergeProps(tooltipProps, { class: "w-full" }), [
                _createVNode(OutlinedButton, {
                  "data-testid": "add-column-button",
                  text: "Adicionar coluna",
                  width: "100%",
                  height: "40px",
                  class: "text-primary border-primary",
                  disabled: allOptionsUsed.value,
                  onClick: addColumn
                }, {
                  prepend: _withCtx(() => [
                    _createVNode(_component_v_icon, {
                      size: "18",
                      class: "mr-[6px]"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(mdiPlus)), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["disabled"])
              ], 16)
            ]),
            _: 1
          }, 8, ["disabled"])
        ])
      ])
    ]),
    _: 1
  }, 8, ["modalActive"]))
}
}

})